import GameService from '~/services/game.service';
import * as gameSelectors from '~/store/game/selectors';
import { PRODUCT_TYPES, CUSTOMER_TYPES } from '~/lib/constants';
import { gameActions, logbookActions, uiActions, financialStatementsActions, inboxActions, loadingActions } from '~/store/actions';
// ---- internal imports ------

import * as types from './actionTypes';
import * as productsSelectors from './selectors';
// ---- local imports ------

async function _saveNewSellingPriceFor(dispatch, productId, newSellingPrice, storeState) {
	const productsSellingPrices = _getProcudersSellingPrices(storeState);
	productsSellingPrices[productId] = newSellingPrice;

	const gameUserId = gameSelectors.getGameId(storeState);

	const result = await GameService.updateProductsSellingPriceDebounced(gameUserId, productsSellingPrices);

	if (!result.isSuccess) {
		dispatch(loadingActions.endAfterNetworkFaliures());
	}
}

function _getProcudersSellingPrices(storeState) {
	let sellingPrices = {};
	const couch = productsSelectors.getCouch(storeState);
	const chair = productsSelectors.getChair(storeState);
	const table = productsSelectors.getTable(storeState);

	sellingPrices[couch.id] = couch.sellingPrice;
	sellingPrices[chair.id] = chair.sellingPrice;
	sellingPrices[table.id] = table.sellingPrice;

	return sellingPrices;
}

export function getActinTypeForSellProduct(productType) {
	switch (productType) {
	case PRODUCT_TYPES.COUCH:
		return types.PRODUCTS_SOLD_COUCH;

	case PRODUCT_TYPES.CHAIR:
		return types.PRODUCTS_SOLD_CHAIR;

	case PRODUCT_TYPES.TABLE:
		return types.PRODUCTS_SOLD_TABLE;

	default:
		throw new Error('unhandled product type');
	}
}

export function productsLoaded(products) {
	return {
		type: types.PRODUCTS_LOADED,
		products: {
			couch: products.couch.toPlainObj(),
			chair: products.chair.toPlainObj(),
			table: products.table.toPlainObj()
		}
	};
}

export function productsSalesDataLoaded(customers) {
	const soldCustomers = customers.filter((customer) => customer.state === 'sold');
	const productSalesForCurrMonth = {
		couch: {
			amount: 0
		},
		chair: {
			amount: 0
		},
		table: {
			amount: 0
		},
	}

	soldCustomers.map((customer) => productSalesForCurrMonth[customer.productName].amount++ );

	return {
		type: types.PRODUCTS_SALES_DATA_LOADED,
		productSalesForCurrMonth,
	}
}

export function updateProductSellingPrice(productType, newSellingPrice) {
	return async (dispatch) => {
		dispatch(uiActions.productSellingPriceChanged(productType));

		switch (productType) {
		case PRODUCT_TYPES.COUCH:
			dispatch(updateCouchSellingPrice(newSellingPrice));
			return;
		case PRODUCT_TYPES.CHAIR:
			dispatch(updateChairSellingPrice(newSellingPrice));
			return;
		case PRODUCT_TYPES.TABLE:
			dispatch(updateTableSellingPrice(newSellingPrice));
			return;
		default:
			throw new Error('unhandled product type');
		}
	};
}

export function updateCouchSellingPrice(newSellingPrice) {
	return async (dispatch, getState) => {
		try {
			dispatch({ type: types.PRODUCTS_UPDATE_COUCH_SELLING_PRICE, newSellingPrice });

			const couch = productsSelectors.getCouch(getState());
			await _saveNewSellingPriceFor(dispatch, couch.id, newSellingPrice, getState());
		} catch (error) {
			console.error(error);
		}
	};
}

export function updateChairSellingPrice(newSellingPrice) {
	return async (dispatch, getState) => {
		try {
			dispatch({ type: types.PRODUCTS_UPDATE_CHAIR_SELLING_PRICE, newSellingPrice });

			const chair = productsSelectors.getChair(getState());
			await _saveNewSellingPriceFor(dispatch, chair.id, newSellingPrice, getState());
		} catch (error) {
			console.error(error);
		}
	};
}

export function updateTableSellingPrice(newSellingPrice) {
	return async (dispatch, getState) => {
		try {
			dispatch({ type: types.PRODUCTS_UPDATE_TABLE_SELLING_PRICE, newSellingPrice });

			const table = productsSelectors.getTable(getState());
			await _saveNewSellingPriceFor(dispatch, table.id, newSellingPrice, getState());
		} catch (error) {
			console.error(error);
		}
	};
}

export function resetProductMonthlySales() {
	return({ type: types.PRODUCTS_RESET_MONTHLY_SALES });
}

export function sellProduct(
	customer,
	customerIndex,
	isTradeshowSale = false,
) {
	return async (dispatch, getState) => {
		let { productName, type } = customer;
		let discount = 0, productCount = 1;
		const isCreditCustomer = type === CUSTOMER_TYPES.CREDIT;

		if (isTradeshowSale) {
			discount =  customer.discount;
			productCount = customer.productCount;
			productName = customer.productType;
		};

		const product = productsSelectors.getProduct(getState(), productName);
		if(!product) {
			throw new Error(`unhandled product type:${productName}`);
		}

		const { amount: productAmountInStock, sellingPrice } = product;

		if(productAmountInStock < productCount) {
			console.warn(`Not enough product amount for product:${productName}`);
			return;
		}

		const sellingPriceAfterDiscount = sellingPrice - (sellingPrice * discount / 100);
		
		const actionType = getActinTypeForSellProduct(productName);
		dispatch({ type: actionType, amount: productAmountInStock - productCount });

		const amountToAddToCash = sellingPriceAfterDiscount * productCount;

		if (!isCreditCustomer) {
			dispatch(gameActions.incrementCash(amountToAddToCash));
		}

		dispatch({ type: types.PRODUCTS_SOLD, value: amountToAddToCash });

		const gameUserId = gameSelectors.getGameId(getState());
		const result = await GameService.sellProduct(
			gameUserId,
			customer.id,
			customerIndex,
			productName, productCount, discount, 
			isTradeshowSale,
			isCreditCustomer,
		);

		if (!result.isSuccess) {
			console.warn("REQUEST FAILED: GameService.sellProduct");
			return;
		}

		const notification = result.data;

		dispatch(inboxActions.handleNewMessage(notification));
		if(!isTradeshowSale) {		
			dispatch(logbookActions.fetchUpdatedLogbook());
		}
	}
}

export function buyProducts(productsAmount) {
	return async (dispatch, getState) => {
		const couch = productsSelectors.getCouch(getState());
		const chair = productsSelectors.getChair(getState());
		const table = productsSelectors.getTable(getState());
		let cashRequired = 0;
		dispatch({ type: types.PRODUCTS_PURCHASED, productsAmount });
		
		if (productsAmount[PRODUCT_TYPES.COUCH] > 0) {
			dispatch({
				type: types.PRODUCTS_COUCH_PURCHASED,
				amount: couch.amount + productsAmount[PRODUCT_TYPES.COUCH]
			});
			cashRequired += productsAmount[PRODUCT_TYPES.COUCH] * couch.purchasingPrice;
		}
		
		if (productsAmount[PRODUCT_TYPES.CHAIR] > 0) {
			dispatch({
				type: types.PRODUCTS_CHAIR_PURCHASED,
				amount: chair.amount + productsAmount[PRODUCT_TYPES.CHAIR]
			});
			cashRequired += productsAmount[PRODUCT_TYPES.CHAIR] * chair.purchasingPrice;
		}
		
		if (productsAmount[PRODUCT_TYPES.TABLE] > 0) {
			dispatch({
				type: types.PRODUCTS_TABLE_PURCHASED,
				amount: table.amount + productsAmount[PRODUCT_TYPES.TABLE]
			});
			cashRequired += productsAmount[PRODUCT_TYPES.TABLE] * table.purchasingPrice;
		}
		
		dispatch(gameActions.incrementCash(-1 * cashRequired));
		
		const gameUserId = gameSelectors.getGameId(getState());
		const productsMap = {
			[table.id]: productsAmount[PRODUCT_TYPES.TABLE],
			[couch.id]: productsAmount[PRODUCT_TYPES.COUCH],
			[chair.id]: productsAmount[PRODUCT_TYPES.CHAIR]
		};
		
		dispatch(loadingActions.start(types.PRODUCTS_PURCHASED));
		
		const result = await GameService.buyProducts(gameUserId, productsMap);

		if (!result.isSuccess) {
			dispatch(loadingActions.endAfterNetworkFaliures());
			console.warn("REQUEST FAILED: GameService.buyProducts");
			return;
		}
		dispatch(loadingActions.end(types.PRODUCTS_PURCHASED));

		const notification = result.data;
		dispatch(inboxActions.handleNewMessage(notification));
		await dispatch(logbookActions.fetchUpdatedLogbook());
	};
}
